import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { supportApi } from "../services/supportApi";
import SupportTicketList from "../components/support/SupportTicketList";
import SupportChat from "../components/support/SupportChat";

const SUPERADMIN_EMAILS = ["johann.t.events@gmail.com", "thuillierjohann@gmail.com"];

const STATUS_FILTERS = ["all", "open", "in_progress", "resolved", "closed"];

const STATUS_COLOR = {
  all: { bg: "#f3f4f6", color: "#374151" },
  open: { bg: "#eef2ff", color: "#6366f1" },
  in_progress: { bg: "#fff7ed", color: "#f59e0b" },
  resolved: { bg: "#f0fdf4", color: "#10b981" },
  closed: { bg: "#f3f4f6", color: "#9ca3af" },
};

const AdminSupportPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = SUPERADMIN_EMAILS.includes(user?.email);

  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [unreadPerTicket, setUnreadPerTicket] = useState({});

  const fetchTickets = useCallback(async () => {
    try {
      const status = activeFilter === "all" ? null : activeFilter;
      const res = await supportApi.adminGetTickets(status);
      setTickets(res.data.tickets || []);
    } catch {
      // access denied or network error
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  const fetchUnread = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await supportApi.adminGetUnreadCount();
      setUnreadPerTicket(res.data.per_ticket || {});
    } catch {}
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    setLoading(true);
    fetchTickets();
  }, [fetchTickets, isSuperAdmin]);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  const handleSelect = useCallback(async (ticketId) => {
    setSelectedId(ticketId);
    try {
      await supportApi.adminMarkRead(ticketId);
      setUnreadPerTicket((prev) => ({ ...prev, [ticketId]: 0 }));
    } catch {}
  }, []);

  if (!isSuperAdmin) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "8px" }}>
        <div style={{ fontSize: "28px", opacity: 0.3 }}>🔒</div>
        <div style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>Access Denied</div>
        <div style={{ fontSize: "13px", color: "#9ca3af" }}>You do not have permission to view this page.</div>
      </div>
    );
  }

  return (
    <Container fluid className="px-0" style={{ height: "calc(100vh - 56px)", overflow: "hidden" }}>
      <Row className="h-100 g-0">

        {/* Left panel */}
        <Col xs={12} md={4} lg={3} style={{ borderRight: "1px solid #f3f4f6", height: "100%", display: "flex", flexDirection: "column", background: "#fff" }}>

          {/* Status filter tabs */}
          <div style={{ borderBottom: "1px solid #f3f4f6", padding: "10px 12px" }}>
            <div style={{ display: "flex", gap: "4px", overflowX: "auto", flexWrap: "nowrap" }}>
              {STATUS_FILTERS.map((f) => {
                const active = activeFilter === f;
                const sc = STATUS_COLOR[f] || { bg: "#f3f4f6", color: "#374151" };
                return (
                  <button
                    key={f}
                    onClick={() => { setActiveFilter(f); setSelectedId(null); }}
                    style={{
                      flexShrink: 0,
                      padding: "4px 10px", borderRadius: "6px",
                      border: active ? `1px solid ${sc.color}33` : "1px solid transparent",
                      background: active ? sc.bg : "transparent",
                      color: active ? sc.color : "#9ca3af",
                      fontSize: "11px", fontWeight: active ? 700 : 500,
                      cursor: "pointer", transition: "all 0.15s",
                      textTransform: "uppercase", letterSpacing: "0.04em",
                    }}
                  >
                    {f === "all" ? t("support.filter_all") : (t(`support.status_${f}`) || f)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Ticket list */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            <SupportTicketList
              tickets={tickets}
              selectedId={selectedId}
              onSelect={handleSelect}
              onNewTicket={null}
              loading={loading}
              unreadPerTicket={unreadPerTicket}
            />
          </div>
        </Col>

        {/* Right panel: chat */}
        <Col xs={12} md={8} lg={9} style={{ height: "100%", background: "#fff" }}>
          {selectedId ? (
            <SupportChat
              key={selectedId}
              ticketId={selectedId}
              isAdmin={true}
              onTicketUpdated={fetchTickets}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "8px" }}>
              <div style={{ fontSize: "32px", opacity: 0.2 }}>🎧</div>
              <span style={{ fontSize: "13px", color: "#9ca3af" }}>{t("support.select_ticket")}</span>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default AdminSupportPage;
